import React, { useEffect, useState } from "react";
import { Download, Monitor, Smartphone, CheckCircle2, Chrome, Globe, Apple, WifiOff, Zap, RefreshCw } from "lucide-react";
import { getInstallState, subscribeInstall, triggerInstall } from "@/lib/installPrompt";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Monitor, title: "Comme un vrai logiciel", desc: "Icône sur le bureau, fenêtre dédiée sans barre de navigateur." },
  { icon: WifiOff, title: "Fonctionne hors-ligne", desc: "Consultez vos données et saisissez même sans Internet." },
  { icon: Zap, title: "Démarrage rapide", desc: "Ouverture directe sur votre tableau de bord." },
  { icon: RefreshCw, title: "Mises à jour automatiques", desc: "Toujours la dernière version, sans rien télécharger." },
];

const GUIDES = [
  {
    icon: Chrome, browser: "Google Chrome (PC / Mac)",
    steps: ["Cliquez sur l'icône d'installation (écran avec flèche ↓) à droite de la barre d'adresse", "Ou : Menu ⋮ → « Caster, enregistrer et partager » → « Installer la page »", "Confirmez avec « Installer » — l'icône apparaît sur votre bureau"],
  },
  {
    icon: Globe, browser: "Microsoft Edge (PC)",
    steps: ["Cliquez sur Menu ⋯ en haut à droite", "Choisissez « Applications » → « Installer ce site en tant qu'application »", "Confirmez — l'app s'épingle au menu Démarrer et au bureau"],
  },
  {
    icon: Apple, browser: "Safari (Mac / iPhone / iPad)",
    steps: ["Sur Mac : Fichier → « Ajouter au Dock »", "Sur iPhone/iPad : bouton Partager (carré avec flèche ↑)", "Choisissez « Sur l'écran d'accueil » puis « Ajouter »"],
  },
  {
    icon: Smartphone, browser: "Android (Chrome)",
    steps: ["Ouvrez le portail dans Chrome", "Menu ⋮ → « Ajouter à l'écran d'accueil » ou « Installer l'application »", "Confirmez — l'icône Mazid Portail apparaît sur votre téléphone"],
  },
];

export default function InstallAppPage() {
  const [state, setState] = useState(getInstallState());

  useEffect(() => subscribeInstall(setState), []);

  const handleInstall = async () => {
    const outcome = await triggerInstall();
    if (outcome === "accepted") toast.success("Installation lancée ! L'icône va apparaître sur votre appareil.");
    else if (outcome === "dismissed") toast.info("Installation annulée. Vous pouvez réessayer à tout moment.");
    else toast.info("Suivez les instructions ci-dessous selon votre navigateur.");
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="install-app-page">
      {/* En-tête + bouton principal */}
      <div className="bg-[#1E3A8A] rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="text-[10px] tracking-[0.25em] text-[#FBBF24] uppercase mb-2">Application de bureau &amp; mobile</div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Installer Mazid Portail</h1>
          <p className="text-sm text-blue-100 mt-2 max-w-xl">
            Installez le portail sur votre ordinateur ou téléphone : il s&apos;ouvre dans sa propre fenêtre,
            directement sur votre tableau de bord, et fonctionne même hors connexion.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {state.installed ? (
              <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-300/40 rounded-xl px-4 py-3" data-testid="install-done-badge">
                <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                <span className="font-semibold text-sm">Application déjà installée sur cet appareil</span>
              </div>
            ) : (
              <button
                onClick={handleInstall}
                className="flex items-center gap-2 bg-[#FBBF24] text-[#1E3A8A] font-bold px-6 py-3 rounded-xl hover:bg-amber-300 transition-colors shadow-lg"
                data-testid="install-now-btn"
              >
                <Download className="h-5 w-5" />
                Installer maintenant
              </button>
            )}
            {!state.installed && !state.canInstall && (
              <span className="text-xs text-blue-200">
                Si rien ne se passe, suivez le guide de votre navigateur ci-dessous.
              </span>
            )}
          </div>
        </div>
        <Monitor className="absolute -right-6 -bottom-8 h-48 w-48 text-white/5" />
      </div>

      {/* Avantages */}
      <div className="grid sm:grid-cols-2 gap-4">
        {BENEFITS.map((b, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
              <b.icon className="h-5 w-5 text-[#1E3A8A]" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm text-[#0F172A]">{b.title}</div>
              <div className="text-xs text-slate-600 mt-1">{b.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Guides par navigateur */}
      <div>
        <h2 className="font-heading font-bold text-lg text-[#0F172A] mb-4">Guide d&apos;installation selon votre navigateur</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {GUIDES.map((g, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`install-guide-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <g.icon className="h-5 w-5 text-[#1E3A8A]" />
                <div className="font-heading font-bold text-sm text-[#0F172A]">{g.browser}</div>
              </div>
              <ol className="space-y-2">
                {g.steps.map((s, j) => (
                  <li key={j} className="flex gap-2 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-full bg-[#FBBF24]/20 text-[#92400E] font-bold flex items-center justify-center flex-shrink-0 text-[10px]">{j + 1}</span>
                    <span className="pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Note : l&apos;application installée est « Mazid Portail » — elle ouvre directement l&apos;espace connecté, pas le site public.
      </p>
    </div>
  );
}
